import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useSWR from 'swr';
import { notificationService } from '../../services/notificationService';
import { ArrowLeft } from 'lucide-react';

const TYPE_ICONS: Record<string, string> = {
  content_rejected: '⚠️',
  contract_created: '📄',
  contract_status: '📋',
  milestone_submitted: '📦',
  milestone_approved: '✅',
  milestone_rejected: '❌',
  trial_goal_updated: '🎯',
  trial_note_updated: '📝',
  new_follower: '👤',
  post_like: '❤️',
  post_comment: '💬',
  payment: '💳',
};

const ACTION_MAP: Record<string, { label: string; getPath: (refId?: string) => string }> = {
  content_rejected: { label: '去申诉', getPath: () => '/profile' },
  contract_created: { label: '查看合同', getPath: (id) => `/contracts/${id}` },
  contract_status: { label: '查看合同', getPath: (id) => `/contracts/${id}` },
  milestone_submitted: { label: '查看合同', getPath: (id) => `/contracts/${id}` },
  milestone_approved: { label: '查看合同', getPath: (id) => `/contracts/${id}` },
  milestone_rejected: { label: '查看合同', getPath: (id) => `/contracts/${id}` },
  trial_goal_updated: { label: '查看试稿', getPath: (id) => `/trials/${id}` },
  trial_note_updated: { label: '查看试稿', getPath: (id) => `/trials/${id}` },
  new_follower: { label: '查看主页', getPath: (id) => `/profile/${id}` },
};

function formatTime(ts: number) {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return '刚刚';
  if (diffMin < 60) return `${diffMin}分钟前`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}小时前`;
  return d.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const SystemMessages: React.FC = () => {
  const { id: notificationId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: notifications = [] } = useSWR(
    'system-messages',
    async () => {
      const res = await notificationService.getNotifications(50);
      return res.success && res.data ? res.data.notifications : [];
    },
    { revalidateOnFocus: false }
  );

  return (
    <div className="max-w-2xl mx-auto min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 z-10">
        <button onClick={() => navigate(-1)} className="p-1 hover:bg-gray-100 rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-semibold text-gray-900">系统消息</h1>
      </div>

      {/* Chat bubble list */}
      <div className="px-4 py-6 space-y-4">
        {notifications.length === 0 ? (
          <div className="text-center text-gray-400 py-20 text-sm">暂无系统消息</div>
        ) : (
          notifications.map((n) => {
            const action = ACTION_MAP[n.type];
            const isHighlighted = n.notificationId === notificationId;

            return (
              <div key={n.notificationId} className="flex gap-3">
                {/* System avatar */}
                <div className="w-9 h-9 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0 text-sm">
                  {TYPE_ICONS[n.type] || '🔔'}
                </div>

                {/* Bubble */}
                <div className="flex-1 max-w-[85%]">
                  <div
                    className={`rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm ${
                      isHighlighted ? 'bg-blue-50 ring-1 ring-blue-200' : 'bg-white'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900">{n.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{n.body}</p>

                    {action && n.referenceId && (
                      <Link
                        to={action.getPath(n.referenceId)}
                        className="inline-block mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {action.label} →
                      </Link>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1 ml-1">{formatTime(n.createdAt)}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SystemMessages;
